import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { X, Zap } from 'lucide-react'
import { dismissQuickAnswerSuggestion, getQuickAnswerSuggestion } from '../../api/conversations'
import { WS_RESYNC_EVENT_TYPE } from '../../lib/realtime/wsClient'
import { useWsEvent } from '../../lib/realtime/wsSessionHooks'

// "Trả lời nhanh: {title}" above the reply box while the AI is off and a Quick Answer entry
// matched the customer's last message (the backend only stores it, nothing is sent).
// The suggestion stays until the customer is answered: filling it in or appending it does not
// clear it, only the backend rules do (staff send, X, AI back on, a new customer message). With
// an empty reply box the text is filled in when the conversation opens; the button shows while
// staff typed something that does not hold the suggestion yet, and a click appends the text on a
// new line. Staff edit and send it themselves. The backend keeps one suggestion per conversation:
// the read API and the `conversation.quick_answer_suggestion` event (null = cleared) feed one
// query cache entry, so the button follows the server. Hidden for users who cannot reply.
//
const LONG_PRESS_MS = 450
const TOUCH_HIDE_MS = 2500

export default function QuickAnswerSuggestion({ tenantId, userId, canReply, replyText, onApply }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // The preview is tied to one suggestion in one conversation: it is stored with that scope
  // and only counts while the scope is unchanged, so a new suggestion or conversation starts closed.
  const [previewScope, setPreviewScope] = useState(null)
  const pressTimer = useRef(null)
  const hideTimer = useRef(null)
  const longPressed = useRef(false)
  const tooltipId = useId()
  const queryKey = ['inbox', tenantId, 'quick-answer-suggestion', userId]
  const enabled = Boolean(canReply && tenantId && userId)

  const { data: suggestion } = useQuery({
    queryKey,
    queryFn: () => getQuickAnswerSuggestion(tenantId, userId),
    enabled,
  })

  // Every event updates its own conversation's cache entry (not only the open one), so a
  // conversation opened later does not show a stale suggestion from the cache.
  useWsEvent('conversation.quick_answer_suggestion', (event) => {
    if (!canReply || !event?.tenant_id || !event?.user_id) return
    queryClient.setQueryData(
      ['inbox', event.tenant_id, 'quick-answer-suggestion', event.user_id],
      event.suggestion ?? null,
    )
  })
  // Events are fire-and-forget; after a reconnect read the server's state again.
  useWsEvent(WS_RESYNC_EVENT_TYPE, () => {
    if (enabled) queryClient.invalidateQueries({ queryKey })
  })

  const clearTimers = () => {
    clearTimeout(pressTimer.current)
    clearTimeout(hideTimer.current)
    pressTimer.current = null
    hideTimer.current = null
  }
  const suggestionKey = suggestion ? `${suggestion.entry_id}:${suggestion.message_id}` : null
  const scope = `${tenantId}|${userId}|${suggestionKey}`
  const preview = previewScope === scope
  const setPreview = (on) => setPreviewScope(on ? scope : null)
  // Pending timers die with the suggestion/conversation and on unmount.
  useEffect(() => {
    longPressed.current = false
    return clearTimers
  }, [scope])

  const report = async (call, action) => {
    // Stop an in-flight read from bringing the suggestion back, then clear optimistically.
    await queryClient.cancelQueries({ queryKey })
    const previous = queryClient.getQueryData(queryKey)
    queryClient.setQueryData(queryKey, null)
    try {
      await call(tenantId, userId)
    } catch (err) {
      Sentry.captureException(err, { tags: { action }, extra: { tenantId, userId } })
      // The server still holds it: put the button back (unless an event already replaced it),
      // then read the server's state to settle.
      if (previous && queryClient.getQueryData(queryKey) === null) queryClient.setQueryData(queryKey, previous)
      queryClient.invalidateQueries({ queryKey })
    }
  }

  // An empty box takes the suggestion without a click, once each time the suggestion's
  // conversation opens: staff who empty the box on purpose are not filled again, but leaving the
  // scope (another conversation, a new suggestion) allows a fill when they come back.
  const autoFilled = useRef(null)
  const shouldAutoFill = Boolean(enabled && suggestion && !replyText.trim())
  // Layout effect: the fill lands before paint, so the button never flashes over an empty box.
  useLayoutEffect(() => {
    if (!shouldAutoFill || autoFilled.current === scope) return
    autoFilled.current = scope
    onApply(suggestion.reply)
  })
  useEffect(() => () => {
    if (autoFilled.current === scope) autoFilled.current = null
  }, [scope])

  // The button is for a box that does not hold the suggestion: staff typed something, or emptied
  // a box that was already filled.
  if (!enabled || !suggestion || replyText.includes(suggestion.reply)) return null

  const handleUse = () => {
    if (longPressed.current) { longPressed.current = false; return }
    onApply(replyText.trim() ? `${replyText}\n${suggestion.reply}` : suggestion.reply)
  }
  const startPress = () => {
    longPressed.current = false
    clearTimers()
    pressTimer.current = setTimeout(() => { longPressed.current = true; setPreview(true) }, LONG_PRESS_MS)
  }
  const endPress = () => {
    clearTimeout(pressTimer.current)
    if (longPressed.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = setTimeout(() => setPreview(false), TOUCH_HIDE_MS)
    }
  }
  // Show on keyboard focus only: a mouse click also focuses the button but the hover
  // preview already covers that.
  const onFocus = (e) => {
    let visible = true
    try { visible = e.currentTarget.matches(':focus-visible') } catch { /* unsupported selector */ }
    if (visible) setPreview(true)
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0, padding: '6px 16px 0', background: 'white' }}>
      {preview && (
        <div id={tooltipId} role="tooltip" style={{ position: 'absolute', bottom: '100%', left: 16, right: 16, maxHeight: 160, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 12, color: '#111', background: 'white', border: '0.5px solid #c7d2fe', borderRadius: 10, padding: '8px 10px', boxShadow: '0 4px 14px rgba(21,53,200,0.12)', zIndex: 5 }}>
          {suggestion.reply}
        </div>
      )}
      <div style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', borderRadius: 16, background: '#eef2ff', border: '0.5px solid #c7d2fe' }}>
        <button
          type="button"
          onClick={handleUse}
          aria-describedby={preview ? tooltipId : undefined}
          onFocus={onFocus}
          onBlur={() => setPreview(false)}
          onPointerEnter={(e) => { if (e.pointerType !== 'touch') setPreview(true) }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch') setPreview(false) }}
          onTouchStart={startPress}
          onTouchEnd={endPress}
          onTouchMove={() => clearTimeout(pressTimer.current)}
          onContextMenu={(e) => e.preventDefault()}
          style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, padding: '5px 4px 5px 10px', border: 'none', background: 'none', color: '#1535c8', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
        >
          <Zap size={12} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('inbox.quickAnswerTitled', { title: suggestion.title })}
          </span>
        </button>
        <button
          type="button"
          onClick={() => report(dismissQuickAnswerSuggestion, 'quick_answer_suggestion_dismiss')}
          aria-label={t('inbox.quickAnswerSuggestionDismiss')}
          style={{ display: 'flex', padding: '5px 8px 5px 4px', border: 'none', background: 'none', color: '#6b7280', cursor: 'pointer' }}
        >
          <X size={12} />
        </button>
      </div>
    </div>
  )
}
